import React from 'react'

import { getDay, startOfToday } from 'date-fns'

import SalesRange from './SalesRange'
import { getDatesFromOffset } from '../../utils'

import { type DateOffset, useSalesOverviewQuery } from '@edge/spark/io-dashboards'
import { CardType } from '@edge/spark/io-dashboards'
import type { BasicWidgetProps } from '../../types'

const WEEK_TEXT = {
    '-1': 'Last',
    0: 'This',
    1: 'Next',
}

const generateOffsetFromWeekModifier = (mod: number): DateOffset => {
    const today = startOfToday()
    const day = getDay(today)
    const start = -day + mod * 7
    return {
        start,
        end: start + 6,
    }
}

const SalesOverview: React.FC<BasicWidgetProps> = ({
    config: {
        weekModifier = 0,
        text = `Overview (${WEEK_TEXT[weekModifier]} Week)`,
        /** If @param offset is passed in, it overrides the week modifier. */
        offset = generateOffsetFromWeekModifier(weekModifier),
    },
    widgetId,
    handleConfig,
    readonly,
}: BasicWidgetProps) => {
    const { startDate, endDate } = getDatesFromOffset(offset, offset)

    const { data, loading } = useSalesOverviewQuery({ variables: { startDate, endDate, dataSource: 'datalake' } })

    const mod = `${WEEK_TEXT[weekModifier]} Week`
    const linkSearchParams = new URLSearchParams({ startDate, endDate, mod })

    const { sold, run, units } = data?.auction?.statistics?.saleStatistics || {}
    const conversion =
        typeof sold === 'number' && typeof run === 'number' && typeof units === 'number' && run > 0
            ? (sold / run) * 100
            : 0

    const link = linkSearchParams
        ? `/salesoverview?${linkSearchParams.toString()}&searchSubject=Sales Overview ${mod}`
        : ''

    return (
        <SalesRange
            cardType={CardType.CUSTOM}
            cardHeaderText={text}
            action={{ text: 'View Sale Overview', link }}
            run={run}
            sold={sold}
            checkIn={units}
            conversion={conversion}
            loading={loading}
            widgetId={widgetId}
            handleConfig={handleConfig}
            readonly={readonly}
        />
    )
}

export default SalesOverview
